/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
	* Unauthorized copying of this file, via any medium is strictly prohibited
	* Proprietary and confidential
	* Written by Aetonix, June 19, 2014
	* For information or permission request, email info@aetonixsystems.com
	*/

var Model = require("../types/Model.js");
var Promise = require("promise");
var extend = require("xtend");
var isObject = require("isobject");
var document = "global".document;
var loadImage = require("../../polyfills/LoadImage.js");
import { isEmpty } from "lodash";

/**
 * @module Messages
 */

module.exports = Model({
	name: "messages",
	_from: null,
	_to: null,

	init: init,
	uninit: uninit,
	listeners: listeners,
	triggers: triggers,

	_contactList: [],
	_managedList: [],

	load_from: load_from,
	load_page: load_page,
	load_next_page: load_next_page,
	send_message: send_message,
	scroll_down: scroll_down,
	defaultMessage: defaultMessage,

	recalculate: recalculate,
	cancel_upload_dialog: cancel_upload_dialog,
	close_upload_dialog: close_upload_dialog,
	clear_pending_message: clear_pending_message,
	close_messages: close_messages,
	handle_page_change: handle_page_change
});

function init() {
	this._from = null;
	this._to = this.config.id;
	this._people = {};

	this.push({
		page: -1, // The current page that is loaded. -1 if there is no page loaded
		from: {}, // The data of the person we're receiving messages from
		to: {}, // Us
		list: [], // List of message objects
		texting: "", // texting message string
		pendingList: [], // List of pending message objects,
		new_content: "", // The new content that should be sent out
		isAvailable: null,
		loadMore: false,
		defaultMessage: "",
	});
}

function uninit() {
	this._from = null;
}

function listeners() {
	return {
		"people:updated": Model.makeRecalculator("people"),
		"mqtt:$/message": handle_new_message,
		"mqtt:$/message/sent": handle_sent_message,
		"availability:updated": handle_availability_change,
		"mqtt:$/message/read/from": handle_message_read,
		"allcontacts:updated": handle_contacts_update,
		"managedusers:updated": handle_managedusers_update,
		"contactprofile:updated": handle_contact_profile_update,
		"managedaccount:updated": handle_managedaccount_update,
		"calling:state:idle": recalculate,
		"meetings:stop": recalculate,
		"desktop:trigger:page": handle_page_change
	};
}

function triggers() {
	return {
		"open": load_from,
		"next": load_next_page,
		"send": send_message,
		"bulk:send": bulk_message,
		"close": close_messages,
		"default": defaultMessage,
		"upload:start": start_upload,
		"upload:confirm": confirm_upload,
		"upload:finish": finish_upload,
		"upload:cancel": cancel_upload_dialog,
		"upload:close": close_upload_dialog
	};
}

async function recalculate() {
	var users = this._people;

	var from = users[this._from];
	var to = users[this._to];

	var texting;
	try {
		var targetId = this._from || this._to;
		const result = await this.api.persiststorage.getData(this._to, { targetId: targetId });
		texting = result.persistStorage?.[0]?.data.message;
	} catch (error) {
		console.error(error);
	}

	var update = {};
	var shouldUpdate = false;

	if (this.data.texting !== texting) {
		update.texting = texting;
		shouldUpdate = true;
	}

	if(this.data.from !== from) {
		update.from = from;
		shouldUpdate = true;
	}

	if(this.data.to !== to) {
		update.to = to;
		shouldUpdate = true;
	}

	if(shouldUpdate)
		this.extend(update);
}

function handle_new_message(message) {
	var from = this._from;
	if (!from) {
		// Show toast message when get new message.
		var fromId = message.from;
		if(!this._people) return;
		var fromUser = this._people[fromId];
		if(!fromUser) return;

		var toastmessage = "messaging.newmessage";

		this.emit("toast:trigger:show", {
			content: toastmessage,
			arguments: fromUser
		});

		return this.dispatch("new", message);
	}

	var is_current_person = message.from === from;

	if (!is_current_person) return;

	var cache = this.data.list;

	this.dispatch("new", message);

	this.extend({
		list: cache.concat(timestampify(message))
	});

	this.scroll_down();

	this.api.messages.read(from);
}

function scroll_down() {
	setTimeout(function () {
		if(document){
			var containers = document.querySelectorAll(".ae-message-history-container");
			var container_array = [].slice.call(containers);
			container_array.forEach(function (element) {
				element.scrollTop = element.scrollHeight;
			});
		}
		this.dispatch("scroll");
	}.bind(this), 500);
}

/**
 * Load a given page from message history
 * @param  {Number} page The page to load. 0 by default
 */
function load_page(page) {
	this.emit("orientationlocker:trigger:lock", { orientation: "portrait" });
	var from = this._from;
	this.emit("save:trigger:start");
	this.defaultMessage(from);
	page = page || 0;
	return this.api.messages.history(from, page)
		.then(add_new_messages.bind(this, page))
		.catch(handle_load_error.bind(this));
}

function add_new_messages(page, list) {
	var cache = this.data.list;
	var loadMore = list.length >= 20;
	var processedList = list.reverse().map(timestampify);

	var update = {
		page: page,
		list: page ? processedList.concat(cache) : processedList,
		loadMore: loadMore
	};
	this.emit("save:trigger:finish");
	return this.extend(update);
}

function load_next_page() {
	var page = this.data.page + 1;
	this.emit("usermetrics:trigger:track", "load more messages in contact's chat", {
		who: this._from,
		page: page
	});
	return this.load_page(page);
}

function handle_message_read(data){
	var who = data.from;
	var time = data.time;
	var from = this._from;
	var list = this.data.list;

	if(from !== who)
		return;

	var newList = list.map(function(message, index){
		if(index === (list.length - 1)){
			var messageUpdate = {
				read: true,
				updated_at: time
			};
			return extend(message, messageUpdate);
		} else {
			return message;
		}
	}).map(timestampify);

	if(newList.length > 0){
		this.extend({
			list: newList
		});
		this.scroll_down();
	}
}

/**
 * Loads messages from a specific person
 * @param  {String} from The id of the person to load messages from
 */
function load_from(from) {
	if (isObject(from)) from = from.from; // When passed in an object

	if(from === this._from)
		return;

	this._from = from;
	var to = this._to;

	Promise.all([
		this.load_page(0),
		this.api.messages.read(from)
	]).then(function () {
		this.emit("people:trigger:add", {
			lean: true,
			who: [from, to]
		});
		this.scroll_down();
	}.bind(this)).catch(console.error.bind(console));

	this.recalculate();
	this.dispatch("opened", {
		from: from
	});

	this.emit("usermetrics:trigger:track", "open contact's chat", {
		who: from,
	});
}

async function defaultMessage(id) {
	var userId;
	if (typeof (id) === "object") {
		userId = id.id;
	} else {
		userId = id;
	}
	const { persistStorage } = await this.api.persiststorage.getData(userId);
	var message = persistStorage[0]?.data || "";
	this.extend({
		defaultMessage: message
	});
}

/**
 * Called from UI with 'out.messages-trigger-send'.  Payload of that message
 * is: destination address (_from) and message text (content) which are passed
 * in as 'data'.
 * @param  {Object} data Two attributes: who:destination address and content:message text
 */
function send_message(data) {
	//called from Ractive/UI by out.message_trigger_send ('send' button)
	//take 'to' from page (this._from), take 'message_content' from page,
	//and call ae-api with payload {to,message_text}

	var message = data.message;
	var who = this._from;
	var popup = false;
	this.extend({
		new_content: "",
		texting: "",
		pendingList: this.data.pendingList.concat(data)
	});
	this.scroll_down();
	this.emit("persiststorage:delete", {
		dataId: who
	});
	this.emit("usermetrics:trigger:track", "send message to contact", {
		who: who
	});
	return this.api.messages.send.message(who, message, popup, this._patientId, this._manageGroup)
		.then(clear_pending_message.bind(this, data))
		.catch(handle_send_error.bind(this, data));
}

/**
 * A function that sends bulk messages with an attachment to a list of contacts.
 * This is triggered with the "messages:trigger:bulk:send" event.
 * @param  {Object} data Two attributes: who:destination address and content:message text
 */
function bulk_message(data) {
	const file = this.data.file;
	const message = data.message;
	const who = data.who;
	const popup = false;


	if(who.length === 0){
		const noContacts = "bulkMessaging.noContacts";
		this.emit("toast:trigger:show", {
			content: noContacts,
			style: "error",
			immediate: true
		});

		this.emit("usermetrics:trigger:track", "attempt to send bulk message but no contacts selected");

		return;
	}

	this.extend({
		new_content: "",
		texting: "",
		pendingList: this.data.pendingList.concat(data)
	});

	this.scroll_down();

	this.emit("usermetrics:trigger:track", "send bulk message to contacts", {
		who: who
	});

	return this.api.messages.send.bulkMessage(who, message, file, popup, this._patientId, this._manageGroup)
		.then(clear_pending_message.bind(this, data))
		.then(() => {this.emit("toast:trigger:show", { content: "bulkMessaging.messageSent", immediate: true});})
		.then(() => {
			this.extend({
				uploading: false,
				file: null,
				fileImage: null
			});
		})
		.catch(handle_send_error.bind(this, data));
}

function start_upload(){
	this.extend({
		uploading: true
	});

	this.emit("usermetrics:trigger:track", "open send attachment to contact's messaging popup", {
		who: this._from
	});
}

function confirm_upload(data){
	var file = data.file;
	loadImage(file).then(function(image){
		this.extend({
			uploading: false,
			file: file,
			fileImage: image
		});
		this.emit("usermetrics:trigger:track", "uploading attachment sent to contact", {
			who: this._from
		});
	}.bind(this));

	this.emit("usermetrics:trigger:track", "select attachment and open file upload confirmation popup", {
		who: this._from
	});
}

function finish_upload(){
	var file = this.data.file;
	var to = this._from;

	var message = {
		file: file
	};

	this.extend({
		pendingList: this.data.pendingList.concat(message)
	});

	this.scroll_down();
	this.cancel_upload_dialog();

	return this.api.messages.send.file(file, to, this._patientId, this._manageGroup)
		.then(clear_pending_message.bind(this, message))
		.then(() =>
		{
			this.extend({
				file: null,
				fileImage: null
			});
		})
		.catch(handle_send_error.bind(this, message));
}

function clear_pending_message(message) {
	var pendingList = this.data.pendingList.filter(function(pendingMessage){
		return pendingMessage !== message;
	});
	this.extend({
		pendingList: pendingList
	});
}

function cancel_upload_dialog(){
	let uploading = this.data.pendingList.length > 0;
	this.extend({
		uploading: false,
		file: null,
		fileImage: null
	});

	if(uploading) this.emit("usermetrics:trigger:track", "uploaded attachment to contact's messages", {
		who: this._from
	});
	else this.emit("usermetrics:trigger:track", "cancel upload attachment to contact's messages", {
		who: this._from
	});
}

function close_upload_dialog(){
	this.extend({
		uploading: false,
	});

	this.emit("usermetrics:trigger:track", "cancel send attachment to contact's messaging", {
		who: this._from
	});
}

function handle_sent_message(message) {
	var currentlyMessaging = this._from;
	if (!currentlyMessaging)
		return;

	var is_current_person = message.to === currentlyMessaging;

	if (!is_current_person) return;

	this.extend({
		list: this.data.list.concat(timestampify(message))
	});
	this.scroll_down();

}

function timestampify(item) {
	var attachment = item.attachment;
	var expires_at;
	if(attachment)
		expires_at = attachment.expires_at;
	return extend(item, {
		expiry: timestamp(expires_at),
		date_expiry: formatDate(expires_at),
		read_timestamp: timestamp(item.updated_at),
		read_formatted_date: formatDate(item.updated_at),
		timestamp: timestamp(item.created_at),
		formatted_date: formatDate(item.created_at)
	});
}

// Converts a date timestamp into a human readable timestamp
function timestamp(value) {
	if (!value) return ""; // Ignore empty values
	var d = new Date(value);
	var res = "";
	res += padNumber(d.getHours());
	res += ":" + padNumber(d.getMinutes());
	return res;
}

// Converts a date timestamp into a human readable date
function formatDate(value) {
	if (!value) return ""; // Ignore empty values
	var d = new Date(value);
	var res = "";
	res += padNumber(d.getDate());
	res += "/" + padNumber(d.getMonth() + 1);
	return res;
}

// Pads numbers less than 10 with a 0
function padNumber(number) {
	if (number < 10) return "0" + number;
	return number;
}

function close_messages() {
	this.emit("orientationlocker:trigger:unlock");

	this._from = null;
	this.extend({
		list: [],
		from: null,
		isAvailable: null,
		uploading: false,
		loadMore: false
	});

	this.emit("usermetrics:trigger:track", "close messages from contact", {
		who: this._from
	});
}

function handle_availability_change(data) {
	if(this._from === null) return;
	var id = this._from;
	var isAvailable = data[id];

	this.extend({
		isAvailable: isAvailable
	});
}

function handle_send_error(message, error) {
	console.error("Couldn't send message: ", error);

	var offlineMessage = "messaging.offline";
	this.emit("toast:trigger:show", {
		content: offlineMessage,
		style: "error",
		immediate: true
	});

	this.clear_pending_message(message);
}

function handle_load_error(error) {
	console.error("Couldn't load message: ", error);
	this.emit("save:trigger:finish");
}

function handle_contacts_update(contacts) {
	var contactList = contacts.merged;
	var from = this.data.from;

	if(isEmpty(from))
		return;

	if(!findContact(contactList, from)){
		this.emit("desktop:trigger:page:back");
		this.close_messages();
	}
}

function handle_managedusers_update(userList) {
	this._managedList = userList;
}

function findContact(contacts, contact){
	return contacts.filter(function(_contact){
		return _contact._id === contact._id;
	})[0];
}

function handle_contact_profile_update(data){
	this._patientId = (data || {}).patientId;
}

function handle_managedaccount_update(data){
	this._manageGroup = (data || {}).group;
}

async function handle_page_change(page) {
	if(page.page === "massmessage") {
		var texting;
		try {
			const result = await this.api.persiststorage.getData(this._to, { targetId: this._to });
			texting = result.persistStorage?.[0]?.data.message;
		} catch (error) {
			console.error(error);
		}
		if (this.data.texting !== texting) {
			var update = {};
			update.texting = texting;
			this.extend(update);
		}
	}
}